import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  AnalyzeResponse,
  RecordItem,
  RecordsResponse,
  SessionGroup,
} from "../types/domain";

/** Fetch the flat list of all records (old behavior). */
export function useRecords() {
  return useQuery<RecordItem[]>({
    queryKey: ["records"],
    queryFn: async () => {
      const data = await api<RecordsResponse["data"]>("/practice/records", { method: "GET" });
      return Array.isArray(data) ? data : [];
    },
  });
}

/** Find a single record inside the records list. */
export function useRecord(recordId: string | undefined): UseQueryResult<RecordItem[]> & {
  record: RecordItem | undefined;
} {
  const records = useRecords();
  const found =
    recordId !== undefined ? records.data?.find((r) => r.recordId === recordId) : undefined;
  return {
    ...records,
    record: found,
  };
}

/** Fetch a single analyze response by sessionId. Backend may expose
 *  GET /practice/sessions/:sessionId returning the original AnalyzeResponse
 *  payload; we use that when available and fall back to records otherwise. */
export function useSessionResult(sessionId: string | undefined) {
  return useQuery<AnalyzeResponse["data"] | null>({
    queryKey: ["session", sessionId],
    enabled: Boolean(sessionId),
    queryFn: async () => {
      if (!sessionId) return null;
      try {
        const data = await api<AnalyzeResponse["data"]>(
          `/practice/sessions/${encodeURIComponent(sessionId)}`,
          { method: "GET" },
        );
        return data ?? null;
      } catch (err) {
        // 404 means the Worker hasn't implemented the dedicated endpoint yet;
        // we'll hydrate from `useRecords` instead.
        if (err instanceof Error && /404/.test(err.message)) return null;
        throw err;
      }
    },
  });
}

/** Group all records by their sessionId into SessionGroup[].
 *  Records without a sessionId land in a synthetic "__legacy__" bucket so
 *  older records still show up in history. */
export function useSessions(): UseQueryResult<RecordItem[]> & { groups: SessionGroup[] } {
  const records = useRecords();

  const groups: SessionGroup[] = (() => {
    const list = records.data ?? [];
    const bySession = new Map<string, RecordItem[]>();
    for (const r of list) {
      const key = r.sessionId ?? "__legacy__";
      const arr = bySession.get(key) ?? [];
      arr.push(r);
      bySession.set(key, arr);
    }
    const out: SessionGroup[] = [];
    for (const [sessionId, chars] of bySession) {
      const createdAt = chars.reduce<string>(
        (acc, c) => (c.createdAt > acc ? c.createdAt : acc),
        chars[0]?.createdAt ?? new Date(0).toISOString(),
      );
      const avgScore =
        chars.reduce((sum, c) => sum + (c.totalScore ?? 0), 0) / Math.max(1, chars.length);
      const weakCount = chars.filter((c) => (c.totalScore ?? 0) < 60).length;
      out.push({
        sessionId,
        imageUrl: chars.find((c) => c.imageUrl)?.imageUrl,
        charCount: chars.length,
        avgScore: Math.round(avgScore),
        weakCount,
        createdAt,
        characters: chars,
      });
    }
    out.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    return out;
  })();

  return {
    ...records,
    groups,
  };
}